import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import PageLayout from "@/components/layout/PageLayout";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Calendar, Clock, ArrowRight, BookOpen, Tag } from "lucide-react";

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  cover_image_url: string | null;
  category: string;
  tags: string[];
  author_name: string;
  reading_time_minutes: number;
  published_at: string | null;
  updated_at: string;
}

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })
    : "";

const Blog = () => {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["blog-posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, slug, title, excerpt, cover_image_url, category, tags, author_name, reading_time_minutes, published_at, updated_at")
        .eq("published", true)
        .order("published_at", { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as BlogPost[];
    },
  });

  const categories = useMemo(() => {
    const set = new Set(posts.map((p) => p.category));
    return Array.from(set);
  }, [posts]);

  const filtered = useMemo(() => {
    let res = posts;
    if (activeCategory) res = res.filter((p) => p.category === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      res = res.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q)),
      );
    }
    return res;
  }, [posts, activeCategory, search]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <PageLayout>
      <Helmet>
        <title>Blog & Wissen – NextInvoice</title>
        <meta
          name="description"
          content="Insights, Anleitungen und Best Practices rund um Geschäftssoftware, ERP, Rechnungen, CRM, Buchhaltung und Digitalisierung im Mittelstand."
        />
        <link rel="canonical" href="https://nxtinvoice.de/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Blog & Wissen – NextInvoice" />
        <meta property="og:description" content="Insights, Anleitungen und Best Practices für KMU und Mittelstand." />
        <meta property="og:url" content="https://nxtinvoice.de/blog" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "NextInvoice Blog",
          description: "Insights, Anleitungen und Best Practices für KMU und Mittelstand.",
          url: "https://nxtinvoice.de/blog",
          inLanguage: "de-DE",
          publisher: { "@type": "Organization", name: "NextInvoice", url: "https://nxtinvoice.de" },
          blogPost: posts.slice(0, 20).map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            description: p.excerpt,
            url: `https://nxtinvoice.de/blog/${p.slug}`,
            datePublished: p.published_at,
            dateModified: p.updated_at,
            author: { "@type": "Person", name: p.author_name },
            image: p.cover_image_url ?? undefined,
          })),
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Startseite", item: "https://nxtinvoice.de/" },
            { "@type": "ListItem", position: 2, name: "Blog", item: "https://nxtinvoice.de/blog" },
          ],
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-br from-primary/8 via-background to-accent/5 py-20 lg:py-28">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, hsl(var(--primary)) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />

        <div className="container relative">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-2">
              <BookOpen className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">NextInvoice Magazin</span>
            </div>
            <h1 className="mb-5 font-display text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl text-balance">
              Wissen, das{" "}
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Ihr Geschäft voranbringt.
              </span>
            </h1>
            <p className="mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-muted-foreground">
              Insights, Anleitungen und Best Practices rund um ERP, Digitalisierung,
              Rechnungswesen und moderne Geschäftsprozesse für den Mittelstand.
            </p>

            {/* Search */}
            <div className="relative mx-auto max-w-xl">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Artikel durchsuchen…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-14 rounded-2xl border-border bg-card pl-12 text-base shadow-lg shadow-primary/5 focus-visible:ring-primary/30"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      {categories.length > 0 && (
        <section className="border-b border-border bg-background/60 py-5 sticky top-20 z-20 backdrop-blur-md">
          <div className="container flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => setActiveCategory(null)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${
                activeCategory === null
                  ? "bg-primary text-primary-foreground shadow"
                  : "bg-muted text-muted-foreground hover:bg-muted/70"
              }`}
            >
              Alle
            </button>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCategory(c === activeCategory ? null : c)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${
                  activeCategory === c
                    ? "bg-primary text-primary-foreground shadow"
                    : "bg-muted text-muted-foreground hover:bg-muted/70"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="container py-16 lg:py-20">
        {isLoading ? (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-96 animate-pulse rounded-2xl bg-muted" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-muted/30 py-20 text-center">
            <BookOpen className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
            <p className="text-muted-foreground">
              {search || activeCategory
                ? "Keine Artikel gefunden."
                : "Noch keine Artikel veröffentlicht. Schauen Sie bald wieder vorbei!"}
            </p>
          </div>
        ) : (
          <>
            {/* Featured */}
            {featured && (
              <Link
                to={`/blog/${featured.slug}`}
                className="group mb-14 grid overflow-hidden rounded-3xl border border-border bg-card shadow-sm transition-all hover:shadow-xl hover:border-primary/30 lg:grid-cols-5"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-primary/15 to-accent/15 lg:col-span-3 lg:aspect-auto">
                  {featured.cover_image_url ? (
                    <img
                      src={featured.cover_image_url}
                      alt={featured.title}
                      loading="eager"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <BookOpen className="h-20 w-20 text-primary/30" />
                    </div>
                  )}
                  <div className="absolute left-5 top-5">
                    <span className="rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wider text-accent-foreground shadow">
                      Featured
                    </span>
                  </div>
                </div>
                <div className="flex flex-col justify-center gap-4 p-8 lg:col-span-2 lg:p-10">
                  <Badge variant="secondary" className="w-fit rounded-full">{featured.category}</Badge>
                  <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-foreground transition-colors group-hover:text-primary lg:text-4xl">
                    {featured.title}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">{featured.excerpt}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{formatDate(featured.published_at)}</span>
                    <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{featured.reading_time_minutes} Min.</span>
                    <span>· {featured.author_name}</span>
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                    Artikel lesen
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            )}

            {/* Grid */}
            {rest.length > 0 && (
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {rest.map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl hover:border-primary/30"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-primary/10 to-accent/10">
                      {post.cover_image_url ? (
                        <img
                          src={post.cover_image_url}
                          alt={post.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <BookOpen className="h-12 w-12 text-primary/30" />
                        </div>
                      )}
                      <div className="absolute left-3 top-3">
                        <Badge className="rounded-full bg-background/90 text-foreground backdrop-blur">
                          {post.category}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col gap-3 p-6">
                      <h3 className="font-display text-xl font-bold leading-snug text-foreground transition-colors group-hover:text-primary">
                        {post.title}
                      </h3>
                      <p className="line-clamp-3 text-sm text-muted-foreground">{post.excerpt}</p>
                      {post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {post.tags.slice(0, 3).map((t) => (
                            <span
                              key={t}
                              className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                            >
                              <Tag className="h-2.5 w-2.5" />
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="mt-auto flex items-center justify-between border-t border-border pt-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1.5">
                          <Calendar className="h-3 w-3" />
                          {formatDate(post.published_at)}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock className="h-3 w-3" />
                          {post.reading_time_minutes} Min.
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </PageLayout>
  );
};

export default Blog;
